if (!customElements.get('collection-filtering-form')) {
class CollectionFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.filterData = [];
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.handleOffDrawerClick = this.handleOffDrawerClick.bind(this);
    this.handleDrawerClose = this.handleDrawerClose.bind(this);
    this.handleDrawerOpen = this.handleDrawerOpen.bind(this);
    this.addDrawerListeners = this.addDrawerListeners.bind(this);
    this.onHistoryChange = this.onHistoryChange.bind(this);
    this.onSortChange = this.onSortChange.bind(this);
    this.onDropDownBlurClick = this.onDropDownBlurClick.bind(this);
    this.productGridId = 'CollectionProductGrid';
    this.filterFormsId = 'CollectionFiltersForm';
    this.searchTemplate = document.querySelector('body.template-search');
    this.collectionTemplate = document.querySelector('body.template-collection')
      || (document.getElementById('CollectionProductGrid') && document.getElementById('CollectionFiltersForm') ? document.body : null);
    this.sections = [];
    this.form = this.querySelector('form');
    this.drawerOpenBtn = document.querySelector('[data-drawer-open-btn]');

    function debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 300).bind(this);

    if (this.form) {
      this.form.addEventListener('input', this.debouncedOnSubmit);
      this.form.addEventListener('change', this.debouncedOnSubmit);
    }

    if (this.drawerOpenBtn) {
      this.drawerOpenBtn.addEventListener('click', this.handleDrawerOpen);
      this.addDrawerListeners();
      window.addEventListener('resize', this.addDrawerListeners);
    }

    window.addEventListener('popstate', this.onHistoryChange);
    document.addEventListener('collection:sort-change', this.onSortChange);

    this.bindActiveFacetButtonEvents();
    document.addEventListener('click', this.onDropDownBlurClick);
  }

  /**
   * disconnectedCallback runs whenever the Theme Editor re-renders this section (each settings
   * change destroys and recreates <collection-filtering-form>). Without this, the window/document
   * listeners bound in the constructor above never got removed, so every edit stacked another full
   * set of them - each history/sort-change/click event then re-ran onHistoryChange/onSortChange/the
   * dropdown-blur scan once per stacked instance, growing without bound for the life of the tab.
   */
  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('input', this.debouncedOnSubmit);
      this.form.removeEventListener('change', this.debouncedOnSubmit);
    }
    if (this.drawerOpenBtn) {
      this.drawerOpenBtn.removeEventListener('click', this.handleDrawerOpen);
      window.removeEventListener('resize', this.addDrawerListeners);
    }
    window.removeEventListener('popstate', this.onHistoryChange);
    document.removeEventListener('collection:sort-change', this.onSortChange);
    document.removeEventListener('click', this.onDropDownBlurClick);
    document.removeEventListener('click', this.handleOffDrawerClick, true);
    if (this._trapFocusKeydownHandler) {
      const menu = document.getElementById('main-collection-filters');
      if (menu) menu.removeEventListener('keydown', this._trapFocusKeydownHandler);
    }
  }

  onDropDownBlurClick(event) {
    const getDropDowns = document.querySelectorAll('details.disclosure-has-popup');
    for (let item of getDropDowns) {
      if (!item.contains(event.target)) {
        item.removeAttribute('open');
      }
    }
  }

  addDrawerListeners() {
    const filterDrawer = document.querySelector('collection-filtering-form');
    if (!filterDrawer) return;
    filterDrawer.removeAttribute('role');
    filterDrawer.removeAttribute('aria-modal');
    filterDrawer.removeAttribute('aria-label');

    // 768px, not 991px: the bottom-sheet CSS and its [data-drawer-open-btn] trigger only
    // exist at max-width 768px. Claiming role="dialog" aria-modal="true" from 769-990px
    // announced the permanently visible desktop sidebar as an open modal with no way to
    // dismiss it, hiding the rest of the page from screen readers (a11y S-07).
    // The accessible name is set here too — an aria-label on a role-less custom element
    // is a prohibited ARIA attribute, so all three attributes move together.
    if (window.innerWidth <= 768) {
      filterDrawer.setAttribute('role', 'dialog');
      filterDrawer.setAttribute('aria-modal', 'true');
      const label = filterDrawer.getAttribute('data-dialog-label');
      if (label) filterDrawer.setAttribute('aria-label', label);
    }

  }

  handleDrawerOpen(e) {
    e.target.setAttribute('tabIndex', '-1');
    const filterDrawer = document.querySelector('collection-filtering-form');
    // querySelectorAll, not querySelector: the sheet has TWO close controls — the header X
    // (.mobile-menu--close-btn) and the sticky "Show N results" CTA (.facets__mobile-apply-btn).
    // The singular lookup only ever matched the X (first in DOM order), so tapping the primary
    // mobile CTA was a genuine no-op: it is type="button" so it doesn't submit either, and
    // handleOffDrawerClick deliberately ignores clicks inside the drawer.
    const drawerCloseBtns = this.querySelectorAll('[data-drawer-close-btn]');
    if (!filterDrawer || !drawerCloseBtns.length) return;

    filterDrawer.classList.add('is-open');
    document.body.classList.add('js-drawer-open');
    this.trapFocus('main-collection-filters', true);
    drawerCloseBtns.forEach((btn) => btn.addEventListener('click', this.handleDrawerClose));

    document.addEventListener('click', this.handleOffDrawerClick, true);
  }

  handleOffDrawerClick(e) {
    const filterDrawer = document.querySelector('collection-filtering-form');
    if (!filterDrawer) return;
    const menuDrawer = document.querySelector('#NavDrawer');
    const isDrawerClick = filterDrawer.contains(e.target) || (menuDrawer && menuDrawer.contains(e.target));

    if (!isDrawerClick) this.handleDrawerClose();
  }

  handleDrawerClose() {
    const filterDrawer = document.querySelector('collection-filtering-form');
    const filterBtn = document.querySelector('[data-drawer-open-btn]');
    if (!filterDrawer || !filterBtn) return;

    document.body.classList.remove('js-drawer-open');

    filterDrawer.classList.remove('is-open');
    filterBtn.setAttribute('tabIndex', '0');
    filterBtn.focus();
    document.removeEventListener('click', this.handleOffDrawerClick, true);

    this.querySelectorAll('[data-drawer-close-btn]').forEach((btn) => btn.removeEventListener('click', this.handleDrawerClose));
  }

  trapFocus(slideout_id, is_initial_open) {
    const idString = `#${slideout_id}`;
    const menu = document.getElementById(slideout_id);
    if (!menu) return;
    const togglers = document.querySelectorAll('.facets__disclosure, .collection-filters__sort');
    const inputs = document.querySelectorAll(`${idString} input:not([type=hidden]), ${idString} button:not([tabindex="-1"]), ${idString} a:not([tabindex="-1"]), ${idString} textarea`);
    const firstInput = inputs[0];
    let lastInput;

    // trapFocus() re-runs on every drawer open (handleDrawerOpen calls it each time) because
    // inputs/togglers must be recomputed against the current facet DOM. A fresh anonymous
    // listener here previously stacked on #main-collection-filters on every open with nothing
    // ever removing it - each keystroke in the drawer re-ran every prior stale closure. Removing
    // the last-attached handler before adding the new one keeps exactly one active at a time.
    if (this._trapFocusKeydownHandler) {
      menu.removeEventListener('keydown', this._trapFocusKeydownHandler);
    }

    this._trapFocusKeydownHandler = function (e) {
      if (!togglers.length) return;
      const getLastToggler = (togglers[togglers.length - 1]);
      const lastGroupCollapsed = !getLastToggler.hasAttribute('open');

      lastInput = lastGroupCollapsed ? getLastToggler : (inputs[inputs.length - 1]);

      if (e.code === 'Tab' && !e.shiftKey && document.activeElement == lastInput) {
        e.preventDefault();
        firstInput.focus();
      }

      if (e.code === 'Tab' && e.shiftKey && document.activeElement == firstInput) {
        e.preventDefault();
        lastInput.focus();
      }
    };

    menu.addEventListener('keydown', this._trapFocusKeydownHandler);
  }


  onSubmitHandler(event) {
    event.preventDefault();
    if (this.collectionTemplate) {
      const form = event.target && event.target.closest('form');
      if (!form) return;
      const searchParams = this.buildCollectionSearchParams(form);
      this.renderPage(searchParams, event);
    }

    if (this.searchTemplate) {
      const formData = new FormData(event.target.closest('form'));
      const searchParamsInitial = new URLSearchParams(window.location.search);

      const getInputs = event.target.closest('form').querySelectorAll('input');
      const getLabels = event.target.closest('form').querySelectorAll('label');

      if (searchParamsInitial.has('type')) {
        const type = searchParamsInitial.get('type');
        formData.append('type', type);
      }

      if (searchParamsInitial.has('q')) {
        const query = searchParamsInitial.get('q');
        formData.append('q', query);
      }

      if (searchParamsInitial.get('type') === 'article,page') {
        for (let item of getInputs) {
          item.setAttribute('disabled', 'disabled');
        }

        for (let inputLabel of getLabels) {
          inputLabel.classList.add('facet-checkbox--disabled');
        }

        if (searchParamsInitial.has('filter.p.product_type')) {
          let newFormData = new URLSearchParams(window.location.search).toString();
          formData.delete('filter.p.product_type');
          this.renderPage(newFormData, event);
        }

        if (searchParamsInitial.has('filter.p.vendor')) {
          let newFormData = new URLSearchParams(window.location.search).toString();
          formData.delete('filter.p.vendor');
          this.renderPage(newFormData, event);
        }

        if (searchParamsInitial.has('filter.v.availability')) {
          let newFormData = new URLSearchParams(window.location.search).toString();
          formData.delete('filter.v.availability');
          this.renderPage(newFormData, event);
        }
      }

      const searchParams = new URLSearchParams(formData).toString();
      this.renderPage(searchParams, event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    this.toggleActiveFacets();
    const href = event.currentTarget && event.currentTarget.href;
    if (!href) return;
    this.renderPage(new URL(href).searchParams.toString());
  }

  onSortChange(event) {
    const searchParams = event.detail && event.detail.searchParams;
    if (!searchParams || !this.collectionTemplate) return;
    this.renderPage(searchParams);
  }

  getRequestBasePath() {
    const localeRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!localeRoot) return window.location.pathname;

    const normalizedRoot = localeRoot.endsWith('/') ? localeRoot.slice(0, -1) : localeRoot;
    const pathname = window.location.pathname;
    if (normalizedRoot && pathname.startsWith(normalizedRoot)) return pathname;
    return `${normalizedRoot}${pathname}`;
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || '';
    this.renderPage(searchParams, null, false);
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  buildCollectionSearchParams(form) {
    const params = new URLSearchParams(new FormData(form));
    if (!params.has('sort_by')) {
      const sortBar = document.querySelector('#sort-by');
      if (sortBar && sortBar.value) params.set('sort_by', sortBar.value);
    }
    params.delete('page');
    return params.toString();
  }

  renderPage(searchParams, event, updateURLHash = true) {
    this.sections = this.getSections();
    if (!this.sections.length) return;

    const gridWrapper = document.querySelector(`#${this.productGridId}>div`);
    if (gridWrapper) gridWrapper.classList.add('loading');
    const requestBasePath = this.getRequestBasePath();

    this.sections.forEach((section) => {
      if (this.collectionTemplate) {
        const url = `${requestBasePath}?section_id=${section.section}&${searchParams}`;

        const filterDataUrl = element => element.url === url;

        this.filterData.some(filterDataUrl) ?
          this.renderSectionFromCache(filterDataUrl, section, event) :
          this.renderSectionFromFetch(url, section, event);
      } else if (this.searchTemplate) {
        const url = `${requestBasePath}?${searchParams}`;
        const filterDataUrl = element => element.url === url;

        this.filterData.some(filterDataUrl) ?
          this.renderSectionFromCache(filterDataUrl, section, event) :
          this.renderSectionFromFetch(url, section, event);
      }
    });

    if (updateURLHash) this.updateURLHash(searchParams);
  }

  renderSectionFromFetch(url, section, event) {
    const gridWrapper = document.querySelector(`#${this.productGridId}>div`);
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        this.filterData = [...this.filterData, { html, url }];

        switch (section.id) {
          case this.productGridId:
            this.renderProductGrid(html);
            break;
          case this.filterFormsId:
            this.renderFilters(html, event);
            break;
          default:
            return;
        }
      })
      .catch((error) => {
        console.error('Filtering error:', error);
        if (gridWrapper) gridWrapper.classList.remove('loading');
      });
  }

  renderSectionFromCache(filterDataUrl, section, event) {
    const html = this.filterData.find(filterDataUrl).html;
    switch (section.id) {
      case this.productGridId:
        this.renderProductGrid(html);
        break;
      case this.filterFormsId:
        this.renderFilters(html, event);
        break;
    }
  }

  renderProductGrid(html) {
    const grid = document.getElementById(this.productGridId);
    if (!grid) return;
    const gridEl = new DOMParser().parseFromString(html, 'text/html').getElementById(this.productGridId);
    if (!gridEl) return;
    const innerHTML = gridEl.innerHTML;

    grid.innerHTML = innerHTML;
    const gridWrapper = grid.querySelector(':scope > div');
    if (gridWrapper) gridWrapper.classList.remove('loading');

    const event = new CustomEvent('paginate');
    document.dispatchEvent(event);
  }

  renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll(`#${this.filterFormsId} .js-filter`);
    const matchesIndex = (element) => element.dataset.index === event?.target.closest('.js-filter')?.dataset.index
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const targetElement = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (targetElement) targetElement.innerHTML = element.innerHTML;
    });

    this.renderActiveFacets(parsedHTML);

    if (countsToRender) {
      const targetFilter = event.target.closest('.js-filter');
      if (targetFilter) this.renderCounts(countsToRender, targetFilter);
    }
  }

  renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) targetElement.innerHTML = activeFacetsElement.innerHTML;
    })

    this.bindActiveFacetButtonEvents();
    this.toggleActiveFacets(false);
  }

  renderCounts(source, target) {
    const countElementSelectors = ['.count-bubble', '.facets__selected'];
    countElementSelectors.forEach((selector) => {
      const targetElement = target.querySelector(selector);
      const sourceElement = source.querySelector(selector);

      if (sourceElement && targetElement) {
        targetElement.outerHTML = sourceElement.outerHTML;
      }
    });
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.addEventListener('click', this.onActiveFilterClick, { once: true });
    });
  }

  updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  getSections() {
    const productGrid = document.getElementById(this.productGridId);
    const filterSection = document.getElementById('main-collection-filters');
    if (!productGrid || !productGrid.dataset.sectionId || !filterSection || !filterSection.dataset.id) {
      return [];
    }

    return [
      {
        id: this.productGridId,
        section: productGrid.dataset.sectionId
      },
      {
        id: this.filterFormsId,
        section: filterSection.dataset.id
      }
    ]
  }
}

  customElements.define('collection-filtering-form', CollectionFiltersForm);
}

if (!customElements.get('price-range-selector')) {
class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', '0');
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

  customElements.define('price-range-selector', PriceRange);
}

if (!customElements.get('page-type-selector')) {
class PageType extends HTMLElement {
  constructor() {
    super()
    const selector = document.querySelector('[data-page-type-selector]');
    if (selector) selector.addEventListener('change', this.setValue.bind(this));
  }

  setValue(e) {
    const selectedTypes = e.currentTarget.querySelectorAll('input:checked');
    const types = Array.from(selectedTypes).map(option => option.value).join(',');

    const typeInput = document.querySelector('#page-type-input');
    if (typeInput) {
        typeInput.value = types;
        const event = new Event('input');
        typeInput.closest('form').dispatchEvent(event);
    }
  }
}

  customElements.define('page-type-selector', PageType);
}
